import styles from './virtualized-select.module.scss';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { ListChildComponentProps, FixedSizeList } from 'react-window';
import CircularProgress from '../progress/progress';
import { t } from 'i18next';

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

export interface Option {
  label: string,
  id: any
}
/* eslint-disable-next-line */
export interface VirtualizedSelectProps {
  options: Array<Option>,
  handleSelect: (id: any) => void,
  selectedOption?: Option,
  placeholder?: string,
  itemSize?: number,
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>, currentList: Option[]) => void,
  isLoading?: boolean
}

export function VirtualizedSelect(props: VirtualizedSelectProps) {
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: Option) => [props, option];
  const filteredOptionsRef = React.useRef<Option[]>(props.options);
  const ITEM_SIZE = props.itemSize || 36;

  const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >(function ListboxComponent(p, ref) {
    const { children, ...other } = p;
    //@ts-ignore
    const options: Array<Option> = children;
    const itemCount = options.length;

    function renderRow(p: ListChildComponentProps) {
      const { data, index, style } = p;
      const itemStyle = {
        ...style
      }
      const dataSet: any = data[index];
      const option: Option = dataSet[1];

      return <li {...dataSet[0]} style={itemStyle}>{option.label}</li>

    }
    const getHeight = () => itemCount > 15 ? 15 * ITEM_SIZE : itemCount * ITEM_SIZE;

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={children}
            height={getHeight()}
            width="100%"
            itemSize={ITEM_SIZE}
            overscanCount={5}
            outerElementType={OuterElementType}
            itemCount={itemCount}>
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (props.onKeyDown) props.onKeyDown(event, filteredOptionsRef.current);
    }
    return (
      <TextField
        {...params}
        variant="filled"
        placeholder={props.placeholder ? props.placeholder : String(t("newCompany.selectCustomer"))}
        value={props.selectedOption?.label}
        hiddenLabel
        onKeyDown={onKeyDown}
      />
    );
  }
  const handleChange = (event: any, value: any, reason: any) => {

    if (reason === 'clear') props.handleSelect(undefined);
    else props.handleSelect(value.id);
  }
  const filterOptions = (options: Option[], { inputValue }: { inputValue: any }) => {
    const inputWords: string[] = inputValue.toLowerCase().split(' ').filter((word: any) => word !== '');
    const firstWord = inputWords[0];

    const optionsInSearch = options.filter(option =>
      inputWords.every(word =>
        option.label.toLowerCase().includes(word)
      )
    ).sort((a, b) => {
      // Check if labels start with the first word
      const startsWithFirstA = a.label.toLowerCase().startsWith(firstWord);
      const startsWithFirstB = b.label.toLowerCase().startsWith(firstWord);

      // Prioritize those that start with the first word
      if (startsWithFirstA && !startsWithFirstB) {
        return -1;
      }
      if (!startsWithFirstA && startsWithFirstB) {
        return 1;
      }

      // If both start with the first word or neither does, sort by label length
      return a.label.length - b.label.length;
    });
    filteredOptionsRef.current = optionsInSearch;
    return optionsInSearch;
  };
  return (
    props.isLoading ? <CircularProgress className='circularProgress' size={24} /> : <Autocomplete
      disableListWrap
      ListboxComponent={ListboxComponent}
      options={props.options}
      value={props.selectedOption || null}
      renderInput={renderInput}
      onChange={handleChange}
      filterOptions={filterOptions}
      //@ts-ignore
      renderOption={renderOption}
      className='d-flex align-items-center'
    />
  );
}
export default VirtualizedSelect;